/**
 * SPRINT 5B: Location Picker Component
 * 
 * Allows buyers to:
 * - Pin their exact delivery location on a map
 * - Drag marker to adjust location
 * - Get current GPS location
 * - See their selected coordinates
 * 
 * Uses Leaflet (free, no API key needed)
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Navigation, Crosshair, AlertCircle, Check } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom delivery pin icon
const deliveryPinIcon = L.divIcon({
  html: `
    <div style="position: relative;">
      <div style="
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #10b981, #059669);
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        animation: pulse 2s infinite;
      "></div>
      <div style="
        position: absolute;
        top: 8px;
        left: 8px;
        font-size: 20px;
        transform: rotate(45deg);
      ">📍</div>
    </div>
    <style>
      @keyframes pulse {
        0%, 100% { transform: rotate(-45deg) scale(1); }
        50% { transform: rotate(-45deg) scale(1.05); }
      }
    </style>
  `,
  className: 'delivery-pin-marker',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

export interface LocationData {
  lat: number;
  lng: number;
  accuracy?: number;
  timestamp?: number;
}

interface LocationPickerProps {
  initialLat?: number;
  initialLng?: number;
  onLocationChange: (location: LocationData) => void;
  onConfirm?: () => void;
  className?: string;
  height?: string;
  disabled?: boolean;
  showConfirmButton?: boolean;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  initialLat = 13.14, // Default to Legazpi City
  initialLng = 123.74,
  onLocationChange,
  onConfirm,
  className = '',
  height = '300px',
  disabled = false,
  showConfirmButton = true,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  
  const [selectedLocation, setSelectedLocation] = useState<LocationData>({
    lat: initialLat,
    lng: initialLng,
  });
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create map with mobile-friendly options
    const map = L.map(mapRef.current, {
      center: [initialLat, initialLng],
      zoom: 16,
      zoomControl: true,
      attributionControl: true,
      // tap options removed due to Leaflet type issues
      touchZoom: true,     // Enable pinch zoom
      dragging: true,      // Enable map dragging
    } as L.MapOptions);

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    // Create draggable marker
    const marker = L.marker([initialLat, initialLng], {
      icon: deliveryPinIcon,
      draggable: !disabled,
    }).addTo(map);

    // Marker drag event
    marker.on('dragend', (e) => {
      const latlng = e.target.getLatLng();
      const newLocation = { lat: latlng.lat, lng: latlng.lng };
      setSelectedLocation(newLocation);
      onLocationChange(newLocation);
    });

    // Map click event - move marker
    map.on('click', (e) => {
      if (disabled) return;
      
      const { lat, lng } = e.latlng;
      marker.setLatLng([lat, lng]);
      const newLocation = { lat, lng };
      setSelectedLocation(newLocation);
      onLocationChange(newLocation);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;
    setMapReady(true);

    // Fix for map not rendering correctly in modals
    // Invalidate size after a short delay to ensure container is visible
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [initialLat, initialLng, disabled]);

  // Update marker when location changes externally
  useEffect(() => {
    if (markerRef.current && mapInstanceRef.current && mapReady) {
      markerRef.current.setLatLng([selectedLocation.lat, selectedLocation.lng]);
    }
  }, [selectedLocation, mapReady]);

  // Get current GPS location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: LocationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        };

        setSelectedLocation(newLocation);
        onLocationChange(newLocation);

        // Pan map to new location
        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.setView([newLocation.lat, newLocation.lng], 17);
          markerRef.current.setLatLng([newLocation.lat, newLocation.lng]);
        }

        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('An unknown error occurred getting your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, [onLocationChange]);

  // Center on current marker
  const centerOnMarker = useCallback(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([selectedLocation.lat, selectedLocation.lng], 17);
    }
  }, [selectedLocation]);

  return (
    <div className={`location-picker ${className}`}>
      {/* Map Container */}
      <div 
        ref={mapRef} 
        style={{ height, width: '100%', borderRadius: '12px', overflow: 'hidden' }}
        className="relative shadow-lg border border-gray-200 dark:border-gray-700"
      />

      {/* Controls - Mobile Optimized */}
      <div className="mt-3 space-y-2">
        {/* Primary Actions Row */}
        <div className="flex gap-2">
          {/* Get Current Location Button */}
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isGettingLocation || disabled}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {isGettingLocation ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden sm:inline">Getting...</span>
              </>
            ) : (
              <>
                <Navigation className="w-5 h-5" />
                <span>Use GPS</span>
              </>
            )}
          </button>

          {/* Center on Marker */}
          <button
            type="button"
            onClick={centerOnMarker}
            disabled={disabled}
            className="flex items-center justify-center gap-2 px-3 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors text-sm"
          >
            <Crosshair className="w-5 h-5" />
            <span className="hidden sm:inline">Center</span>
          </button>
        </div>

        {/* Confirm Button - Full Width */}
        {showConfirmButton && onConfirm && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={disabled}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-bold transition-colors text-base"
          >
            <Check className="w-5 h-5" />
            Confirm This Location
          </button>
        )}
      </div>

      {/* Location Error */}
      {locationError && (
        <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{locationError}</span>
        </div>
      )}

      {/* Selected Coordinates Display */}
      <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <MapPin className="w-4 h-4 text-emerald-500" />
          <span className="font-medium">Selected Location:</span>
          <span className="font-mono">
            {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
          </span>
        </div>
        {selectedLocation.accuracy && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Accuracy: ~{Math.round(selectedLocation.accuracy)}m
          </div>
        )}
      </div>

      {/* Instructions */}
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        📍 Tap on the map or drag the pin to set your exact delivery location
      </p>
    </div>
  );
};

export default LocationPicker;

